/**
 * Price-failure record (merchant-facing diagnostics only).
 *
 * UpdatePrice can fail in two shapes and, before this module, neither told
 * anyone anything useful:
 *
 *  1. The parsed formula throws when evaluated (e.g. a label left stranded in
 *     the string). Caught by UpdatePrice's catch and posted to `logs/error`,
 *     which returns nothing in production — so effectively invisible.
 *  2. The formula evaluates to NaN (e.g. a division by an empty number input).
 *     No throw at all: `Math.max(min, NaN)` is NaN, the animate branch is
 *     skipped by its own `!isNaN` guard, and the ticker silently keeps the
 *     price it already had — usually 0.
 *
 * Both leave a shopper looking at a stale or zero price. This module records
 * which happened, and the substituted expression that produced it, so the
 * builder can show the merchant that there is no price rather than a wrong
 * one, and hand the expression to Calcy.
 *
 * NOTHING here changes what a shopper sees: it writes a state field and
 * dispatches an event. The one place behaviour differs is the preview
 * (`appState.cpc_isPreview`), which only ever runs inside the merchant's
 * builder — see `renderPriceFailureInPreview`.
 */
import { appState } from "../state/app-state";
import $ from "jquery";

export type PriceFailureReason = "not-a-number" | "error";

export interface PriceFailure {
  reason: PriceFailureReason;
  /** The formula after substitution, exactly as it was handed to eval. */
  expression: string;
  /** Thrown message, when the failure was a throw. */
  message?: string;
}

/** Placeholder shown in the builder preview in place of a price we don't have. */
export const NO_PRICE_TEXT = "—";

/** Fired on `document` whenever the failure state changes. */
export const PRICE_FAILURE_EVENT = "cpc:price-failure";

/**
 * The most recent failure, or null when the last calculation produced a price.
 * Read by the builder; never read by pricing code.
 */
export function getPriceFailure(): PriceFailure | null {
  return currentFailure;
}

let currentFailure: PriceFailure | null = null;

function announce(): void {
  // Preview only. A shopper's page must behave exactly as it does today, so
  // nothing observable — not even an event nobody listens to — is emitted
  // on a real storefront.
  if (appState.cpc_isPreview !== true) return;
  try {
    document.dispatchEvent(
      new CustomEvent(PRICE_FAILURE_EVENT, { detail: currentFailure }),
    );
  } catch {
    // CustomEvent is unavailable in some very old embedded webviews. A missing
    // diagnostic must never break pricing, so this is deliberately swallowed.
  }
}

export function recordPriceFailure(failure: PriceFailure): void {
  currentFailure = failure;
  announce();
  renderPriceFailureInPreview();
}

export function clearPriceFailure(): void {
  if (currentFailure === null) return;
  currentFailure = null;
  announce();
}

/**
 * Builder preview only: replace the stale/zero number with a dash, so the
 * merchant can see there is no price rather than being shown a wrong one.
 *
 * Guarded on `cpc_isPreview`, which `src/index.ts` sets only when
 * `window.Shopify` is absent — i.e. never on a real storefront. Changing what
 * a shopper sees when pricing fails is a separate decision with its own risk
 * (a wrong price becomes no price, and no add-to-cart), and is not made here.
 */
export function renderPriceFailureInPreview(): void {
  if (appState.cpc_isPreview !== true) return;
  var ticker = $("#priceTicker");
  if (ticker.length === 0) return;
  ticker.text(NO_PRICE_TEXT);
}
